import { zodResolver } from "@hookform/resolvers/zod"
import { Sparkles } from "lucide-react"
import * as React from "react"
import { Controller, useForm } from "react-hook-form"
import { toast } from "sonner"
import { z } from "zod"
import { ApiError } from "@/api/client"
import { type Job, type JobInput, jobsApi } from "@/api/jobs"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"

const SLUG_RE = /^[a-z0-9-]+$/

function slugify(input: string): string {
  return input
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/ı/g, "i")
    .replace(/ş/g, "s")
    .replace(/ğ/g, "g")
    .replace(/ü/g, "u")
    .replace(/ö/g, "o")
    .replace(/ç/g, "c")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 100)
}

const schema = z
  .object({
    title: z.string().min(3, "En az 3 karakter.").max(120),
    slug: z
      .string()
      .min(1, "Slug zorunlu.")
      .max(100)
      .regex(SLUG_RE, "Sadece küçük harf, rakam ve tire."),
    department: z.string().max(60),
    location: z.string().max(120),
    employment_type: z.enum(["full_time", "part_time", "contract", "internship"]),
    description: z.string().max(20000),
    requirements: z.string().max(20000),
    salary_min: z.string(),
    salary_max: z.string(),
    currency: z.enum(["TRY", "USD", "EUR", "GBP"]),
  })
  .refine(
    (v) => {
      const min = v.salary_min ? Number(v.salary_min) : null
      const max = v.salary_max ? Number(v.salary_max) : null
      if (min !== null && (Number.isNaN(min) || min < 0)) return false
      if (max !== null && (Number.isNaN(max) || max < 0)) return false
      return true
    },
    { message: "Maaş alanları sayısal ve sıfırdan büyük olmalı." },
  )
  .refine(
    (v) => {
      const min = v.salary_min ? Number(v.salary_min) : null
      const max = v.salary_max ? Number(v.salary_max) : null
      return min === null || max === null || max >= min
    },
    { message: "Maksimum maaş minimumdan küçük olamaz.", path: ["salary_max"] },
  )

type FormValues = z.infer<typeof schema>

const EMPLOYMENT_LABELS: Record<string, string> = {
  full_time: "Tam zamanlı",
  part_time: "Yarı zamanlı",
  contract: "Sözleşmeli",
  internship: "Staj",
}

export function JobForm({
  initial,
  submitLabel,
  onSubmit,
  onCancel,
}: {
  initial?: Job
  submitLabel: string
  onSubmit: (values: JobInput) => Promise<void>
  onCancel?: () => void
}) {
  const [error, setError] = React.useState<string | null>(null)
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      title: initial?.title ?? "",
      slug: initial?.slug ?? "",
      department: initial?.department ?? "",
      location: initial?.location ?? "",
      employment_type: initial?.employment_type ?? "full_time",
      description: initial?.description ?? "",
      requirements: initial?.requirements ?? "",
      salary_min: initial?.salary_min != null ? String(initial.salary_min) : "",
      salary_max: initial?.salary_max != null ? String(initial.salary_max) : "",
      currency: initial?.currency ?? "TRY",
    },
  })

  const slugTouched = React.useRef(Boolean(initial?.slug))
  const title = form.watch("title")
  React.useEffect(() => {
    if (!slugTouched.current) form.setValue("slug", slugify(title || ""))
  }, [title, form])

  const [drafting, setDrafting] = React.useState(false)
  async function generateDraft() {
    const title = form.getValues("title").trim()
    if (title.length < 3) {
      toast.error("Önce bir ilan başlığı gir.")
      return
    }
    setDrafting(true)
    try {
      const r = await jobsApi.draft({
        title,
        department: form.getValues("department") || null,
        location: form.getValues("location") || null,
        employment_type: form.getValues("employment_type"),
      })
      form.setValue("description", r.description, { shouldDirty: true })
      form.setValue("requirements", r.requirements, { shouldDirty: true })
      toast.success("Taslak hazır (Hirefy AI).")
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Taslak üretilemedi.")
    } finally {
      setDrafting(false)
    }
  }

  async function handle(values: FormValues) {
    setError(null)
    try {
      await onSubmit({
        title: values.title,
        slug: values.slug,
        department: values.department || null,
        location: values.location || null,
        employment_type: values.employment_type,
        description: values.description || null,
        requirements: values.requirements || null,
        salary_min: values.salary_min ? Number(values.salary_min) : null,
        salary_max: values.salary_max ? Number(values.salary_max) : null,
        currency: values.currency,
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : "Kaydedilemedi.")
    }
  }

  return (
    <form className="space-y-4" onSubmit={form.handleSubmit(handle)} noValidate>
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      <Card>
        <CardContent className="grid gap-4 p-6 md:grid-cols-2">
          <div className="space-y-1.5 md:col-span-2">
            <Label htmlFor="title">İlan başlığı</Label>
            <Input id="title" {...form.register("title")} placeholder="Senior Frontend Developer" />
            {form.formState.errors.title && (
              <p className="text-xs text-destructive">{form.formState.errors.title.message}</p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="slug">Slug</Label>
            <Input
              id="slug"
              {...form.register("slug", {
                onChange: () => {
                  slugTouched.current = true
                },
              })}
              placeholder="senior-frontend-developer"
            />
            {form.formState.errors.slug && (
              <p className="text-xs text-destructive">{form.formState.errors.slug.message}</p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="employment_type">Çalışma şekli</Label>
            <Controller
              control={form.control}
              name="employment_type"
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger id="employment_type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(EMPLOYMENT_LABELS).map(([v, label]) => (
                      <SelectItem key={v} value={v}>
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="department">Departman</Label>
            <Input id="department" {...form.register("department")} placeholder="Engineering" />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="location">Lokasyon</Label>
            <Input id="location" {...form.register("location")} placeholder="İstanbul / Remote" />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="salary_min">Min. maaş</Label>
            <Input id="salary_min" type="number" {...form.register("salary_min")} />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="salary_max">Maks. maaş</Label>
            <Input id="salary_max" type="number" {...form.register("salary_max")} />
            {form.formState.errors.salary_max && (
              <p className="text-xs text-destructive">{form.formState.errors.salary_max.message}</p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="currency">Para birimi</Label>
            <Controller
              control={form.control}
              name="currency"
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger id="currency">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {["TRY", "USD", "EUR", "GBP"].map((c) => (
                      <SelectItem key={c} value={c}>
                        {c}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
          </div>

          <div className="md:col-span-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={generateDraft}
              disabled={drafting}
            >
              <Sparkles className="mr-1.5 size-3.5" />
              {drafting ? "Hirefy AI üretiyor…" : "Hirefy AI ile tanım ve gereksinimleri üret"}
            </Button>
          </div>

          <div className="space-y-1.5 md:col-span-2">
            <Label htmlFor="description">İş tanımı</Label>
            <Textarea
              id="description"
              {...form.register("description")}
              rows={6}
              placeholder="Pozisyonu, ekibi ve sorumlulukları anlat."
            />
          </div>

          <div className="space-y-1.5 md:col-span-2">
            <Label htmlFor="requirements">Gereksinimler</Label>
            <Textarea
              id="requirements"
              {...form.register("requirements")}
              rows={6}
              placeholder="Aranan nitelikler, teknolojiler, deneyim."
            />
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end gap-2">
        {onCancel && (
          <Button type="button" variant="outline" onClick={onCancel}>
            Vazgeç
          </Button>
        )}
        <Button type="submit" disabled={form.formState.isSubmitting}>
          {submitLabel}
        </Button>
      </div>
    </form>
  )
}
